/**
 * The navigation marks.
 *
 * Drawn rather than imported: a jewellery back office has its own vocabulary —
 * a scale, a tag, a crucible, a ledger — and none of it exists in a generic
 * icon set. They are single-stroke and share one grid so they read as a family
 * at 16px, which is the only size they are ever used at.
 */
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const Frame = ({ children }: { children: React.ReactNode }) => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0" aria-hidden="true" {...stroke}>
    {children}
  </svg>
);

export const Icons = {
  /** A counter till. */
  dashboard: () => (
    <Frame>
      <path d="M3 4h14v5H3zM3 12h6v4H3zM11 12h6v4h-6z" />
    </Frame>
  ),
  /** A price tag with its hole — how every piece in the shop is labelled. */
  sell: () => (
    <Frame>
      <path d="M10.5 3H17v6.5L9 17.5 2.5 11z" />
      <circle cx="14" cy="6" r="1.1" />
    </Frame>
  ),
  invoice: () => (
    <Frame>
      <path d="M5 2.5h10v15l-2.5-1.5L10 17.5 7.5 16 5 17.5z" />
      <path d="M8 7h4M8 10.5h4" />
    </Frame>
  ),
  /** Two arrows trading places: metal in, jewellery out. */
  exchange: () => (
    <Frame>
      <path d="M3 7h11l-3-3M17 13H6l3 3" />
    </Frame>
  ),
  customer: () => (
    <Frame>
      <circle cx="10" cy="7" r="3" />
      <path d="M4 17c0-3.3 2.7-5 6-5s6 1.7 6 5" />
    </Frame>
  ),
  purchase: () => (
    <Frame>
      <path d="M3 5h2l2 8h8l2-6H6" />
      <circle cx="8" cy="16" r="1.1" />
      <circle cx="14" cy="16" r="1.1" />
    </Frame>
  ),
  /** A crucible: what old gold becomes. */
  oldGold: () => (
    <Frame>
      <path d="M5 6h10l-1.5 7a2 2 0 0 1-2 1.5h-3A2 2 0 0 1 6.5 13z" />
      <path d="M8 3.5c0 1 2 1 2 2M12 3.5c0 1-1.5 1-1.5 2" />
    </Frame>
  ),
  supplier: () => (
    <Frame>
      <path d="M2.5 8.5 10 4l7.5 4.5V15L10 17.5 2.5 15z" />
      <path d="M10 9.5V17.5M2.5 8.5 10 12l7.5-3.5" />
    </Frame>
  ),
  product: () => (
    <Frame>
      <path d="M10 2.5 17 6v8l-7 3.5L3 14V6z" />
    </Frame>
  ),
  /** A ring in its box — one tagged piece. */
  item: () => (
    <Frame>
      <circle cx="10" cy="12" r="4" />
      <path d="M8 8.5 10 5l2 3.5" />
    </Frame>
  ),
  transfer: () => (
    <Frame>
      <path d="M3 6h9M9 3l3 3-3 3M17 14H8m3-3-3 3 3 3" />
    </Frame>
  ),
  adjust: () => (
    <Frame>
      <path d="M4 10h12M7 6.5h9M7 13.5h9" />
      <circle cx="4.5" cy="6.5" r="1.2" />
      <circle cx="4.5" cy="13.5" r="1.2" />
    </Frame>
  ),
  /** A balance scale: the physical count. */
  audit: () => (
    <Frame>
      <path d="M10 3v14M6 17h8M4 8h5l-2.5 4.5z" />
      <path d="M11 8h5l-2.5 4.5z" />
    </Frame>
  ),
  jobWork: () => (
    <Frame>
      <path d="M12.5 3.5a3.5 3.5 0 0 0-4.6 4.6L3 13v3h3l4.9-4.9a3.5 3.5 0 0 0 4.6-4.6L13.5 9 11 6.5z" />
    </Frame>
  ),
  melting: () => (
    <Frame>
      <path d="M7 3c0 2.5 3 3 3 5.5S7 12 7 14a3.5 3.5 0 0 0 7 0c0-3-3-4-3-7" />
    </Frame>
  ),
  rate: () => (
    <Frame>
      <path d="M3 14l4-5 3.5 3L17 5" />
      <path d="M13 5h4v4" />
    </Frame>
  ),
  payment: () => (
    <Frame>
      <path d="M2.5 6h15v9h-15z" />
      <path d="M2.5 9.5h15" />
    </Frame>
  ),
  cash: () => (
    <Frame>
      <path d="M2.5 5.5h15v9h-15z" />
      <circle cx="10" cy="10" r="2.2" />
    </Frame>
  ),
  ledger: () => (
    <Frame>
      <path d="M5 3h10v14H5z" />
      <path d="M8 6.5h4M8 10h4M8 13.5h2" />
    </Frame>
  ),
  expense: () => (
    <Frame>
      <path d="M10 3v14M13.5 6H8.5a2 2 0 0 0 0 4h3a2 2 0 0 1 0 4H6" />
    </Frame>
  ),
  income: () => (
    <Frame>
      <path d="M10 16V4M6 8l4-4 4 4" />
      <path d="M4 17h12" />
    </Frame>
  ),
  report: () => (
    <Frame>
      <path d="M4 16V9M8 16V4M12 16v-5M16 16V7" />
    </Frame>
  ),
  approval: () => (
    <Frame>
      <path d="M4 10.5 8 14l8-8" />
    </Frame>
  ),
  period: () => (
    <Frame>
      <path d="M3.5 5h13v12h-13z" />
      <path d="M3.5 8.5h13M7 3v3M13 3v3" />
    </Frame>
  ),
  user: () => (
    <Frame>
      <circle cx="10" cy="6.5" r="2.8" />
      <path d="M4.5 16.5c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" />
    </Frame>
  ),
  search: () => (
    <Frame>
      <circle cx="9" cy="9" r="5.5" />
      <path d="M13 13l4 4" />
    </Frame>
  ),
  menu: () => (
    <Frame>
      <path d="M3 6h14M3 10h14M3 14h14" />
    </Frame>
  ),
};

export type IconName = keyof typeof Icons;
